import { FC } from "react";
import { Redeem } from "@mui/icons-material";
import { pad, toBytes, toHex } from "viem";

import type { IServerSignature } from "@dzambalaorg/types-blockchain";
import { useMetamask, useServerSignature, useWriteContractWithSimulation } from "@dzambalaorg/react-hooks-eth";
import { ListAction, ListActionVariant } from "@framework/styled";
import {
  AddressType,
  type IAchievementItemReport,
  type IAchievementRule,
  type IContract,
  TokenType,
} from "@framework/types";
import ExchangeClaimFacetClaimABI from "@framework/abis/json/ExchangeClaimFacet/claim.json";
import { ZeroAddress } from "@framework/constants";

import { sorter } from "../../../../../utils/sorter";

interface IAchievementRedeemButtonProps {
  achievementRule: IAchievementRule;
  className?: string;
  count: IAchievementItemReport;
  disabled?: boolean;
  variant?: ListActionVariant;
}

export const AchievementRedeemButton: FC<IAchievementRedeemButtonProps> = props => {
  const { achievementRule, className, count = { count: 0 }, disabled, variant = ListActionVariant.button } = props;

  const { writeContractAsync } = useWriteContractWithSimulation();

  const referrer = ZeroAddress;

  const levelsNotRedeemed = achievementRule.levels.filter(lvl => lvl.redemptions?.length === 0);

  // const redeemableLevels = achievementRule.levels.filter(lvl => lvl.amount <= count.count);

  // Level to achieve = lowest not redeemed level
  const achievementLevel = levelsNotRedeemed.sort(sorter("achievementLevel"))[0];
  // const achievementLevel = levelsNotRedeemed.reduce((foundLevel, nextLevel) => {
  //   if (nextLevel.amount > count.count && nextLevel.achievementLevel > foundLevel.achievementLevel) {
  //     return nextLevel;
  //   }
  //   return foundLevel;
  // }, levelsNotRedeemed[0]);

  const previousLevels = achievementLevel
    ? achievementRule.levels.filter(({ amount }) => amount < achievementLevel.amount)
    : [];
  const previousLevelsNotRedeemed = previousLevels.length
    ? previousLevels.some(({ redemptions }) => !redemptions?.length)
    : true;

  const metaFnWithSign = useServerSignature(
    async (_values: null, sign: IServerSignature, systemContract: IContract) => {
      return writeContractAsync({
        address: systemContract.address as AddressType,
        abi: ExchangeClaimFacetClaimABI,
        functionName: "claim",
        args: [
          {
            externalId: sign.bytecode, // claimEntity ID
            expiresAt: sign.expiresAt,
            nonce: toBytes(sign.nonce, { size: 32 }),
            extra: pad(toHex("0"), { size: 32 }),
            receiver: achievementRule.contract!.merchant!.wallet,
            referrer: ZeroAddress,
          },
          achievementLevel.reward?.components.map(component => ({
            tokenType: Object.values(TokenType).indexOf(component.tokenType),
            token: component.contract!.address,
            tokenId:
              component.contract!.contractType === TokenType.ERC1155
                ? component.template!.tokens![0].tokenId
                : (component.templateId || 0).toString(), // suppression types check with 0
            amount: component.amount,
          })),
          sign.signature,
        ],
      });
    },
  );

  const { execute: metaFn } = useMetamask(() => {
    return metaFnWithSign(
      {
        url: "/achievements/sign",
        method: "POST",
        data: {
          referrer,
          achievementLevelId: achievementLevel.id,
        },
      },
      null,
    ) as Promise<void>;
  });

  const handleRedeem = async () => {
    await metaFn().finally(() => {
      // TODO reload page
      // window.location.reload();
    });
  };

  return (
    <ListAction
      onClick={handleRedeem}
      icon={Redeem}
      message="form.buttons.redeemLvl"
      messageValues={
        !disabled
          ? { level: achievementLevel ? `LVL: ${achievementLevel.achievementLevel.toString()}` : "" }
          : { level: "" }
      }
      className={className}
      dataTestId="AchievementRedeemButton"
      disabled={
        disabled ||
        !achievementLevel ||
        (!previousLevelsNotRedeemed &&
          (!!achievementLevel.redemptions?.length || count.count < achievementLevel.amount))
      }
      variant={variant}
    />
  );
};
